import { notification } from 'antd'
import i18next from 'i18next'
import { action, makeAutoObservable } from 'mobx'

import { ordersApi } from '../../api'
import { OrderKeys } from '../../data'
import { ViewOrderUiStore } from './ViewOrderUiStore'

const getKey = (key: string) => `${OrderKeys.orderModule}.api.${key}`

export class ViewOrderApiStore {
  constructor(protected readonly uiStore: ViewOrderUiStore) {
    makeAutoObservable(this)
  }

  @action
  async updateOrder() {
    if (!this.uiStore.selectedOrder || !this.uiStore.hashFormState || !this.uiStore.selectedOrder.id) {
      return false
    }

    const actualUrls = await this.uiStore.fileList.processFiles(this.uiStore.selectedOrder)

    if (!actualUrls || !this.uiStore.location.pickPointType) {
      return false
    }

    try {
      const data = await this.uiStore.orderStore.updateOrder({
        id: this.uiStore.selectedOrder.id,
        description: this.uiStore.location.description.trim(),
        courierWay: this.uiStore.location.courierWay.trim(),
        house: this.uiStore.location.house,
        street: this.uiStore.location.street,
        latitude: this.uiStore.location.coordinates.latitude,
        longitude: this.uiStore.location.coordinates.longitude,
        timetable: JSON.stringify(
          this.uiStore.workConditions.timetable.getTimetable(
            this.uiStore.location.pickPointType,
            this.uiStore.location.floorLevel,
          ),
        ),
        area: this.uiStore.workConditions.area || undefined,
        warehouseArea: this.uiStore.workConditions.warehouseArea || undefined,
        dressingRoomsCount: this.uiStore.workConditions.dressingRoomsCount || undefined,
        cellingHeight: this.uiStore.workConditions.cellingHeight || undefined,
        actualUrls,
        comment: this.uiStore.comment.trim(),
        pickPointType: this.uiStore.location.pickPointType,
        subsidyHexType: this.uiStore.location.subsidyHexTypeItem?.value,
        couriersAccess: this.uiStore.location.couriersAccess,
      })

      notification.success({
        message: i18next.t(getKey('updateOrderSuccess'), { count: this.uiStore.selectedOrder.id }),
      })

      return data
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('updateOrderError')),
        description: (error as Error).message,
      })
      return false
    }
  }

  @action
  async deleteOrder(orderId: number) {
    try {
      const { data } = await ordersApi.deleteOrder(orderId)
      void this.uiStore.getOrderList()
      return data
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('deleteOrderError')),
        description: (error as Error).message,
      })
      return false
    }
  }

  @action
  async addOrderPayment(orderId: number) {
    try {
      const res = await ordersApi.addOrderPayment(orderId)
      this.uiStore.setOrderPaymentInfo(res)
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('addOrderPaymentError')),
        description: (error as Error).message,
      })
    }
  }

  @action
  async checkOrderPayment(orderId: number) {
    try {
      const res = await ordersApi.checkOrderPayment(orderId)
      this.uiStore.setOrderPaymentStatus(res.status)
      return res
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('checkOrderPaymentError')),
        description: (error as Error).message,
      })
    }
  }

  @action
  async getOrderMessages(id: number) {
    try {
      const res = await ordersApi.getOrderMessages(id)
      this.uiStore.setMessages(res)
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('getOrderMessagesError')),
        description: (error as Error).message,
      })
    }
  }

  @action
  async confirmOrder(id: number, willOpen: boolean) {
    try {
      await ordersApi.confirmOrder(id, willOpen)
      return true
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('confirmOrderError')),
        description: (error as Error).message,
      })
      return false
    }
  }

  @action
  async assignReferralCode(id: number, referralCode: string) {
    try {
      return await ordersApi.assignReferralCode(id, referralCode)
    } catch (error) {
      notification.error({ message: (error as Error).message })
      return false
    }
  }

  async getFurnitureOrderType(id: number) {
    try {
      const result = await ordersApi.getFurnitureOrderType(id)
      return result?.orderScheme
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('getFurnitureOrderTypeError')),
        description: (error as Error).message,
      })
    }
  }

  async getPaymentDeadline(id: number) {
    try {
      const result = await ordersApi.getPaymentDeadline(id)
      return result.seconds
    } catch (error) {
      /* Отображение ошибки не нужно */
      console.error(`Не удалось получить дедлайн оплаты заявки ${(error as Error).message}`)
    }
  }
}
